import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import HeroSection from './components/HeroSection';
import CategoryChips from './components/CategoryChips';
import LocationToggle from './components/LocationToggle';
import ListingsGrid from './components/ListingsGrid';
import AuthPromptModal from './components/AuthPromptModal';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { businessService } from '../../services/businessService';
import { categoryService } from '../../services/categoryService';
import { favoritesService } from '../../services/favoritesService';

const HomeDirectory = () => {
  const [listings, setListings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [isLocalFirst, setIsLocalFirst] = useState(true);
  const [userLocation, setUserLocation] = useState('');
  const [authPrompt, setAuthPrompt] = useState({ isOpen: false, action: 'save' });
  const [error, setError] = useState('');
  const [totalBusinessCount, setTotalBusinessCount] = useState(0);
  
  const navigate = useNavigate();
  const { user } = useAuth();

  // Initialize data - Load ALL businesses for homepage directory
  useEffect(() => {
    const initializeData = async () => {
      setIsLoading(true);
      setError('');
      
      try {
        // Get user location from localStorage or browser
        const savedLocation = localStorage?.getItem('userLocation');
        if (savedLocation) {
          setUserLocation(savedLocation);
        }
        
        // Load categories and ALL businesses concurrently
        const [categoriesResult, businessesResult] = await Promise.all([
          categoryService?.getCategories(),
          businessService?.getAllBusinesses({ 
            sortBy: isLocalFirst ? 'distance' : 'created_at',
            limit: 100  // Optional performance limit - show first 100 businesses
          })
        ]);

        if (categoriesResult?.error) {
          console.error('Categories error:', categoriesResult?.error);
          setError('Failed to load categories');
        } else {
          setCategories(categoriesResult?.data || []);
        }

        if (businessesResult?.error) {
          console.error('Businesses error:', businessesResult?.error);
          setError('Failed to load business directory');
        } else {
          const businessesWithFavorites = await enrichWithFavorites(businessesResult?.data || []);
          setListings(businessesWithFavorites);
          setTotalBusinessCount(businessesResult?.totalCount || businessesWithFavorites?.length);
        }

      } catch (error) {
        console.error('Initialization error:', error);
        setError('Failed to load business directory. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    initializeData();
  }, [isLocalFirst, user]);

  // Enrich businesses with favorite status
  const enrichWithFavorites = async (businesses) => {
    if (!user || !businesses?.length) return businesses;

    try {
      const enrichedBusinesses = await Promise.all(
        businesses?.map(async (business) => {
          const { isFavorited } = await favoritesService?.isFavorited(user?.id, business?.id);
          return {
            ...business,
            isFavorited: isFavorited || false
          };
        })
      );
      return enrichedBusinesses;
    } catch (error) {
      console.error('Error enriching with favorites:', error);
      return businesses;
    }
  };

  // Handle category selection - Load all businesses in category
  const handleCategorySelect = async (categoryId) => {
    setSelectedCategory(categoryId);
    setIsLoading(true);
    setError('');
    
    try {
      const { data, error: businessError, totalCount } = await businessService?.getAllBusinesses({
        categoryId: categoryId === 'all' ? null : categoryId,
        sortBy: isLocalFirst ? 'distance' : 'created_at',
        limit: 100  // Performance limit
      });

      if (businessError) {
        setError('Failed to filter businesses');
      } else {
        const enrichedData = await enrichWithFavorites(data || []);
        setListings(enrichedData);
        setTotalBusinessCount(totalCount || enrichedData?.length);
      }
    } catch (error) {
      console.error('Category filter error:', error);
      setError('Failed to filter businesses');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle location toggle - Reload all businesses with new sorting
  const handleLocationToggle = () => {
    setIsLocalFirst(!isLocalFirst);
    setIsLoading(true);
    
    // Re-fetch with new sorting
    handleCategorySelect(selectedCategory);
  };

  // Handle location request
  const handleLocationRequest = () => {
    if (navigator?.geolocation) {
      navigator?.geolocation?.getCurrentPosition(
        (position) => {
          // Mock reverse geocoding for now
          const mockLocation = "Camden, London";
          setUserLocation(mockLocation);
          localStorage?.setItem('userLocation', mockLocation);
        },
        (error) => {
          console.error('Error getting location:', {
            code: error?.code,
            message: error?.message,
            details: error
          });
          
          // Provide specific error messages based on error codes
          let fallbackLocation = "London, UK";
          let errorMessage = "Location access not available";
          
          switch(error?.code) {
            case error?.PERMISSION_DENIED:
              errorMessage = "Location access denied by user";
              break;
            case error?.POSITION_UNAVAILABLE:
              errorMessage = "Location information unavailable";
              break;
            case error?.TIMEOUT:
              errorMessage = "Location request timed out";
              break;
            default:
              errorMessage = "Unknown location error";
              break;
          }
          
          console.warn(`Geolocation Error: ${errorMessage}. Using fallback location: ${fallbackLocation}`);
          
          // Fallback to manual input or default
          setUserLocation(fallbackLocation);
          localStorage?.setItem('userLocation', fallbackLocation);
        },
        {
          enableHighAccuracy: false,
          timeout: 10000, // 10 second timeout
          maximumAge: 300000 // 5 minute cache
        }
      );
    } else {
      console.warn('Geolocation is not supported by this browser. Using fallback location.');
      setUserLocation("London, UK");
      localStorage?.setItem('userLocation', "London, UK");
    }
  };

  // Handle save listing
  const handleSave = async (listingId, action) => {
    if (!user) {
      setAuthPrompt({ isOpen: true, action: 'save' });
      return;
    }
    
    try {
      if (action === 'login-required') {
        setAuthPrompt({ isOpen: true, action: 'save' });
        return;
      }
      
      const { isFavorited, error } = await favoritesService?.toggleFavorite(user?.id, listingId);
      
      if (error) {
        console.error('Toggle favorite error:', error);
        return;
      }

      // Update local state
      setListings(prev => 
        prev?.map(listing => 
          listing?.id === listingId 
            ? { ...listing, isFavorited }
            : listing
        )
      );
    } catch (error) {
      console.error('Save listing error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header Navigation */}
      <Header />
      
      {/* Hero Section with proper spacing for fixed header */}
      <div className="pt-16">
        <HeroSection />
      </div>
      
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="AlertCircle" size={16} className="text-destructive" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          </div>
        )}

        {/* Location Toggle */}
        <LocationToggle
          isLocalFirst={isLocalFirst}
          onToggle={handleLocationToggle}
          userLocation={userLocation}
          onLocationRequest={handleLocationRequest}
        />

        {/* Category Navigation */}
        <CategoryChips
          categories={categories}
          selectedCategory={selectedCategory}
          onCategorySelect={handleCategorySelect}
        />

        {/* Listings Section */}
        <section id="listings-section" className="scroll-mt-24">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-heading font-bold text-foreground mb-2">
                {selectedCategory === 'all' ? 'Complete Business Directory' 
                  : `${categories?.find(c => c?.id === selectedCategory)?.name || 'Category'} Directory`
                }
              </h2>
              <p className="text-muted-foreground font-body">
                {selectedCategory === 'all' 
                  ? `Showing all ${totalBusinessCount} business${totalBusinessCount !== 1 ? 'es' : ''} ${userLocation ? `• Local results near ${userLocation}` : '• Across the UK'}`
                  : `Showing all ${totalBusinessCount} ${categories?.find(c => c?.id === selectedCategory)?.name?.toLowerCase() || 'category'} business${totalBusinessCount !== 1 ? 'es' : ''} ${userLocation ? `near ${userLocation}` : 'across the UK'}`
                }
              </p>
            </div>
            
            <button
              onClick={() => navigate('/search-results')}
              className="flex items-center space-x-2 px-4 py-2 text-primary hover:text-primary/80 transition-gentle"
            >
              <span className="text-sm font-body font-medium">Advanced Search</span>
              <Icon name="ArrowRight" size={16} />
            </button>
          </div>

          <ListingsGrid
            listings={listings}
            isLoading={isLoading}
            onLoadMore={() => {}} // No load more needed - showing all businesses
            hasMore={false} // No pagination - showing all
            onSave={handleSave}
            isAuthenticated={!!user}
          />
        </section>

        {/* Featured Categories */}
        <section className="mt-16 py-12 bg-card rounded-2xl">
          <div className="px-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-4">
                Explore Wellness Categories
              </h2>
              <p className="text-muted-foreground font-body max-w-2xl mx-auto">
                Browse our complete directory by category to find exactly what you're looking for in your wellness journey.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {categories?.slice(0, 8)?.map((category) => (
                <button
                  key={category?.id}
                  onClick={() => handleCategorySelect(category?.id)}
                  className="p-4 bg-background rounded-lg hover:bg-primary/5 transition-organic text-left group"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center group-hover:bg-primary/20 transition-organic">
                      <Icon 
                        name={category?.slug?.includes('meditation') ? 'Brain' : 
                              category?.slug?.includes('yoga') ? 'Activity' :
                              category?.slug?.includes('energy') ? 'Zap' :
                              category?.slug?.includes('therapy') ? 'Heart' : 'Sparkles'} 
                        size={20} 
                        className="text-primary" 
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-body font-semibold text-foreground group-hover:text-primary transition-gentle">
                        {category?.name}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Show All Categories Link */}
            {categories?.length > 8 && (
              <div className="text-center mt-6">
                <button
                  onClick={() => setSelectedCategory('all')}
                  className="text-primary hover:text-primary/80 font-body font-medium transition-gentle"
                >
                  View All {categories?.length} Categories →
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Business Statistics */}
        {totalBusinessCount > 0 && (
          <section className="mt-16 py-8 text-center">
            <div className="bg-primary/5 rounded-2xl p-8">
              <h3 className="text-xl font-heading font-bold text-foreground mb-2">
                Complete Business Directory
              </h3>
              <p className="text-muted-foreground font-body mb-4">
                Browse our comprehensive directory of {totalBusinessCount} wellness businesses across the UK
              </p>
              <div className="flex items-center justify-center space-x-6 text-sm">
                <div className="flex items-center space-x-1">
                  <Icon name="MapPin" size={16} className="text-primary" />
                  <span className="font-body">UK-wide coverage</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="Star" size={16} className="text-primary" />
                  <span className="font-body">Verified listings</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="Clock" size={16} className="text-primary" />
                  <span className="font-body">Updated daily</span>
                </div>
              </div>
            </div>
          </section>
        )}
      </main>
      
      {/* Auth Prompt Modal */}
      <AuthPromptModal
        isOpen={authPrompt?.isOpen}
        onClose={() => setAuthPrompt({ isOpen: false, action: 'save' })}
        action={authPrompt?.action}
      />
    </div>
  );
};

export default HomeDirectory;